/* eslint-disable no-unused-vars */
import {
  createTheme,
  Fab,
  Grid,
  ThemeProvider,
  StyledEngineProvider,
  TextField,
  useMediaQuery,
  ClickAwayListener,
  CircularProgress,
} from '@mui/material';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import Picker from 'emoji-picker-react';
import makeStyles from '@mui/styles/makeStyles';
import React, { useEffect, useRef, useState } from 'react';
import propTypes from 'prop-types';
import { isString, toLower, toUpper } from 'lodash';
import SendIcon from '@mui/icons-material/Send';
import collections from '../../../constants/FirestoreCollections';
import { useFirestore, useFirebase } from 'react-redux-firebase';
import { AccountBox, EmojiEmotions, AttachFile } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { actions } from '../../../actions';
import { Box } from '@mui/system';
import axios from 'axios';
import { messagesTypes } from '../../../constants/ChatMessages';
import { searchAndGetTemplate } from '../helpers/index';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { ARAUCARIA, ASISTIRAC, BEAUTY, NOBIS, SUMA } from '../../../constants/organizations';
const MySwal = withReactContent(Swal);

const useStyles = (small) => {
  return makeStyles((theme) => ({
    main: {
      position: 'absolute',
      alignItems: 'center',
      padding: '0 10px',
      overflow: 'hidden',
      minHeight: '70px',
      maxHeight: '120px',
      background: '#F0F0F0',
      // position: 'absolute',
      bottom: small ? '56px' : 0,
      zIndex: 1000,
    },
    messageLy: {
      flex: 1,
      margin: '16px 0',
    },
    sendButton: {
      position: 'relative',
      width: '40px',
      background: '#ffcc01',
      color: theme.palette.white.main,
      margin: '0 8px',
      height: '30px',
      padding: '0 8px',
      '&:hover': {
        backgroundColor: '#e0b402',
        '& svg': {
          transform: 'scale(1.3)',
        },
      },
      '& svg': {
        position: 'absolute',
        transition: 'all .3s',
      },
    },
  }));
};

const theme = createTheme({
  overrides: {
    MuiFormLabel: {
      root: {
        color: '#000000',
      },
    },
  },
});

const filter = createFilterOptions();
function MessageInput(props) {
  const { isOlderChat, handleSendMessage, sending, chat, chatId } = props;

  // define styles
  const small = useMediaQuery('(max-width:600px)');
  const classes = useStyles(small)();

  // states
  const [message, setMessage] = useState('');
  const [openEmojis, setOpenEmojis] = useState(false);
  const [allTemplates, setAllTemplates] = useState([]);
  const [value, setValue] = useState('');

  // hooks
  const firestore = useFirestore();
  const firebase = useFirebase();
  const organization = useSelector((state) => state.app.organization);
  const templates = useSelector((state) => state.app.templates);
  const _message = useSelector((state) => state.app.message);
  const profile = useSelector((state) => state.firebase.profile);

  const appDispatch = useDispatch();
  const inputFile = useRef(null);
  useEffect(() => {
    if (isString(_message)) {
      setMessage(_message);
      setValue(_message);
    }
  }, [_message]);

  useEffect(() => {
    const filteredTemplates = templates
      .filter(
        (template) =>
          template.global ||
          template.organizationId === organization.id ||
          template.id === organization.id,
      )
      .filter(
        (template) =>
          !((isNobis || isAraucaria || isBeauty || isSuma || isAsistirAC) && template.global),
      )
      .filter((template) => !message || toUpper(template.value).indexOf(toUpper(message)) !== -1)
      .sort((a, b) => {
        try {
          const normalizeValue = (value) => {
            try {
              if (!value || typeof value !== 'string') return '';
              return value
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '') // Remover diacríticos
                .replace(/[()[\]]/g, '') // Remover paréntesis y corchetes
                .replace(/\s+/g, ' ') // Normalizar espacios
                .trim()
                .toUpperCase();
            } catch (normalizeError) {
              console.warn('Error normalizando valor:', value, normalizeError);
              return String(value || '').toUpperCase();
            }
          };

          const aValue = normalizeValue(a?.value);
          const bValue = normalizeValue(b?.value);

          return aValue.localeCompare(bValue, 'es', {
            sensitivity: 'base',
            numeric: true,
          });
        } catch (sortError) {
          console.warn('Error en ordenamiento de templates:', sortError);
          const aFallback = String(a?.value || '').toUpperCase();
          const bFallback = String(b?.value || '').toUpperCase();
          return aFallback < bFallback ? -1 : aFallback > bFallback ? 1 : 0;
        }
      });

    setAllTemplates(filteredTemplates);
  }, [organization, message]);

  // methods
  const onKeyDown = (event) => {
    if (isDisablePanelUserMessaging) return;
    if (
      (event.key === 'ArrowUp' || event.key === 'ArrowDown') &&
      (message.length < 95 || value.length < 95)
    )
      setMessage('');
    if ((event.key === 'Enter' || event._reactName === 'onClick') && message !== '') {
      event.preventDefault();
      event.stopPropagation();
      if (sending) return;

      handleSendMessage(event, message);
      setValue('');
      setMessage('');
    }
  };

  // redux
  // eslint-disable-next-line no-unused-vars
  const projectId = process.env.REACT_APP_PROJECT_ID;
  const ref = firebase.storage().ref();

  // custom cases
  const isAdmin = profile?.claims?.includes('admin');
  const isDisablePanelUserMessaging =
    organization?.businessRules?.disablePanelUserMessaging && !isAdmin;
  const onEmojiClick = (event, emojiObject) => {
    setValue(value + emojiObject.emoji);
  };

  const sendImage = (file) => {
    if (isDisablePanelUserMessaging) return;
    const isPDF = toLower(file.type).indexOf('pdf') !== -1;
    const random = Math.floor(Math.random() * 10000);
    const route = `images/${organization.id}/${random}-${file.name}`;
    ref
      .child(route)
      .put(file)
      .then(() => {
        ref
          .child(route)
          .getDownloadURL()
          .then((fileURL) => {
            appDispatch({
              type: actions.changeLoading,
              loading: { loading: true, priority: 1 },
            });
            if (chat.type === 1) {
              axios({
                url: `https://us-central1-${projectId}.cloudfunctions.net/gateway`,
                headers: {
                  'Content-Type': 'application/json',
                },
                method: 'post',
                data: {
                  to: chat.client.phone,
                  provider: organization.provider,
                  message: fileURL,
                  organizationId: profile.organizationId,
                  email: profile.email,
                  type: isPDF ? 'file' : 'image',
                },
              }).finally(() => {
                appDispatch({
                  type: actions.changeLoading,
                  loading: { loading: false, priority: 1 },
                });
              });
            } else {
              firestore
                .collection(collections.chats)
                .doc(chatId)
                .update({
                  messages: firestore.FieldValue.arrayUnion({
                    message: fileURL,
                    creationDate: firestore.Timestamp.fromDate(new Date()),
                    type: messagesTypes.user,
                    contentType: isPDF ? 'file' : 'image',
                    email: profile.email,
                  }),
                })
                .finally(() => {
                  appDispatch({
                    type: actions.changeLoading,
                    loading: { loading: false, priority: 1 },
                  });
                });
            }
          });
      });
  };

  const handleChangeFile = (e) => {
    if (e.target.files.length > 0) {
      const file = e.target.files[0];
      if (!['pdf', 'png', 'jpg', 'jpeg'].find((e) => toLower(file.type).indexOf(e) !== -1)) {
        return appDispatch({
          type: actions.alert,
          alert: {
            snackbarOpen: true,
            snackbarMessage: `Los tipos de archivos soportados son: .pdf, .jpeg, .png, .jpg`,
            snackbarSeverity: 'error',
          },
        });
      }

      if (file.size / 1000 > 64) {
        MySwal.fire({
          text: 'La imagen supera 64KB. Se le cobraras gastos adicionales. ¿Esta seguro que desea continuar?',
          showConfirmButton: true,
          showDenyButton: true,
          confirmButtonText: 'SI',
          denyButtonText: 'NO',
          icon: 'info',
        }).then((result) => {
          if (result.isConfirmed) {
            sendImage(file);
          }
        });
      } else {
        sendImage(file);
      }
    }
  };

  const isNobis = NOBIS.includes(organization.id);
  const isAraucaria = ARAUCARIA.includes(organization.id);
  const isSuma = SUMA.includes(organization.id);
  const isBeauty = BEAUTY.includes(organization.id);
  const isAsistirAC = ASISTIRAC.includes(organization.id);

  const getTemplateLabel = (template) => {
    const templateMessage =
      template.message.length > 90 ? template.message.slice(0, 90) + ' ...' : template.message;
    return `[${toUpper(template.value)}] ${templateMessage}`;
  };

  const handleChangeAutoComplete = (event, newValue, reason) => {
    try {
      if (reason === 'clear') {
        setValue('');
        setMessage('');
        return;
      }

      if (typeof newValue === 'string') {
        setValue(newValue);
        setMessage(newValue);
      } else if (newValue && typeof newValue === 'object' && newValue.id) {
        const templateMessage = searchAndGetTemplate(newValue.id, allTemplates);
        setValue(templateMessage);
        setMessage(templateMessage);
      } else if (!newValue) {
        setValue('');
        setMessage('');
      }
    } catch (error) {
      console.warn('handleChangeAutoComplete', error);
    }
  };

  const handleFilterOptions = (options, params) => {
    try {
      if (message.length > 95 || value.length > 95) return [];
      const filtered = filter(options, params);
      return filtered;
    } catch (error) {
      console.log('error en MessageInpunt/handleFilterOptions', error);
    }
  };

  return (
    <Grid container className={classes.main}>
      <Grid item className={classes.messageLy}>
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={theme}>
            <input autoComplete="false" name="hidden" type="text" style={{ display: 'none' }} />
            {openEmojis && (
              <ClickAwayListener
                onClickAway={(event) => {
                  setOpenEmojis(false);
                }}
              >
                <Box
                  zIndex={10}
                  bottom="72px"
                  right={0}
                  position="fixed"
                  sx={{ background: 'red' }}
                >
                  <Picker onEmojiClick={onEmojiClick} />
                </Box>
              </ClickAwayListener>
            )}
            <Autocomplete
              size="small"
              value={value}
              disabled={isOlderChat || isDisablePanelUserMessaging}
              onChange={handleChangeAutoComplete}
              inputValue={value}
              onInputChange={(event, newInputValue, reason) => {
                if (event?.type === 'change') {
                  setValue(newInputValue);
                  setMessage(newInputValue);
                } else if (reason === 'clear') {
                  setValue('');
                  setMessage('');
                }
              }}
              onKeyDown={onKeyDown}
              filterOptions={handleFilterOptions}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                return getTemplateLabel(option);
              }}
              isOptionEqualToValue={(option, value) => {
                if (typeof option === 'string') return option === value;

                return option.id === value || getTemplateLabel(option) === value;
              }}
              clearOnBlur
              options={allTemplates}
              sx={{ maxHeight: 100, overflowY: 'auto' }}
              freeSolo
              renderInput={(params) => (
                <TextField
                  multiline
                  minRows={1}
                  maxRows={20}
                  {...params}
                  label="Escriba un mensaje"
                />
              )}
            />
          </ThemeProvider>
        </StyledEngineProvider>
      </Grid>
      {!!(organization && organization.businessRules && organization.businessRules.sendImages) && (
        <Grid id="popup-btn" item align="left" sx={{ position: 'relative', height: '100%' }}>
          <Fab
            className={classes.sendButton}
            onClick={() => inputFile.current.click()}
            disabled={sending || isOlderChat || isDisablePanelUserMessaging}
          >
            <AttachFile />
          </Fab>
          <input
            onChange={handleChangeFile}
            type="file"
            ref={inputFile}
            style={{ display: 'none' }}
          />
        </Grid>
      )}
      {isAdmin && (
        <Grid id="popup-btn" item align="left">
          <Fab
            className={classes.sendButton}
            aria-label="send"
            onClick={() => {
              appDispatch({
                type: actions.openPatientPopup,
                patientPopup: { open: true },
              });
            }}
          >
            <AccountBox />
          </Fab>
        </Grid>
      )}
      <Grid item align="left">
        <Fab
          className={classes.sendButton}
          aria-label="send"
          onClick={() => setOpenEmojis(!openEmojis)}
          disabled={sending || isOlderChat || isDisablePanelUserMessaging}
        >
          <EmojiEmotions />
        </Fab>
      </Grid>
      <Grid item align="left">
        <Fab
          className={classes.sendButton}
          aria-label="send"
          onClick={onKeyDown}
          disabled={sending || isOlderChat || isDisablePanelUserMessaging}
        >
          {sending ? <CircularProgress size={24} color="inherit" /> : <SendIcon />}
        </Fab>
      </Grid>
    </Grid>
  );
}

const Props = {
  isOlderChat: propTypes.bool,
  handleSendMessage: propTypes.func,
  sending: propTypes.bool,
  chat: propTypes.object,
  chatId: propTypes.string,
};

MessageInput.propTypes = Props;

export default MessageInput;
